import React, { useState, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, ShieldCheck, CreditCard, UserCog, FileText, Lock, Volume2, VolumeX, GraduationCap, Building2, Bell, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card';
import { useAuth } from '../hooks/useAuth';
import { useAccessControl } from '../contexts/AccessControlContext';
import { usePremiumContext } from '../contexts/PremiumContext';
import { getSoundEnabled, setSoundEnabled } from '../utils/sound';
import { useInstitutions } from '../hooks/useInstitutions';
import { useNotifications } from '../hooks/useNotifications';
import { isNativeApp } from '../utils/platform';

interface SettingsOption {
  title: string;
  description: string;
  to: string;
  icon: React.ReactNode;
}

export const SettingsPage: React.FC = () => {
  const { user, loading } = useAuth();
  const { premiumMode } = useAccessControl();
  const { isStudent, isCorporate } = usePremiumContext();
  const [soundEnabled, _setSoundEnabled] = useState(getSoundEnabled);
  const { institutions, toggleRepresent } = useInstitutions({ userId: user?.id, isStudent, isCorporate });
  const {
    enabled: notificationsEnabled,
    reminderTime,
    enableNotifications,
    disableNotifications,
    updateReminderTime,
  } = useNotifications();

  const toggleSound = useCallback(() => {
    const next = !soundEnabled;
    _setSoundEnabled(next);
    setSoundEnabled(next);
  }, [soundEnabled]);

  // Define all possible settings options
  const allSettingsOptions: SettingsOption[] = [
    {
      title: 'Account Management',
      description: 'Update your username, email address, or password.',
      to: '/settings/account',
      icon: <UserCog className="h-5 w-5 text-primary" />
    },
    {
      title: 'Subscription',
      description: 'See your plan status and manage billing through Stripe.',
      to: '/settings/subscription',
      icon: <ShieldCheck className="h-5 w-5 text-primary" />
    },
    {
      title: 'Payment Methods',
      description: 'Update the cards on file using the Stripe Customer Portal.',
      to: '/settings/payment-methods',
      icon: <CreditCard className="h-5 w-5 text-primary" />
    },
    {
      title: 'Terms & Conditions',
      description: 'Review the latest Wallstreetle usage terms.',
      to: '/policies/terms',
      icon: <FileText className="h-5 w-5 text-primary" />
    },
    {
      title: 'Privacy Policy',
      description: 'Understand how we collect, use, and protect your data.',
      to: '/policies/privacy',
      icon: <Lock className="h-5 w-5 text-primary" />
    }
  ];

  // Filter settings options based on premium mode
  // Only show Subscription and Payment Methods in STANDARD mode
  const settingsOptions = allSettingsOptions.filter(option => {
    if (premiumMode !== 'STANDARD') {
      return option.title !== 'Subscription' && option.title !== 'Payment Methods';
    }
    return true;
  });

  // Redirect to home if not authenticated
  if (!loading && !user) {
    return <Navigate to="/popular" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 py-12 px-4">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-8">
        <div className="space-y-3 text-left">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary/80">
            Settings
          </p>
          <h1 className="text-3xl font-bold text-foreground md:text-4xl">
            Manage your Wallstreetle experience
          </h1>
          <p className="max-w-2xl text-base text-muted-foreground">
            Access account controls, subscription management, legal documents, and billing tools all in one place.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {settingsOptions.map(option => (
            <Link
              key={option.to}
              to={option.to}
              className="group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Card className="h-full transition-transform duration-150 group-hover:-translate-y-1 group-hover:shadow-lg">
                <CardHeader className="flex flex-row items-start gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    {option.icon}
                  </div>
                  <div className="space-y-1">
                    <CardTitle className="text-lg font-semibold text-foreground">
                      {option.title}
                    </CardTitle>
                    <CardDescription>
                      {option.description}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="flex items-center justify-between pt-0">
                  <span className="text-sm font-medium text-primary group-hover:underline">
                    Go to {option.title}
                  </span>
                  <ArrowRight className="h-4 w-4 text-primary transition-transform duration-150 group-hover:translate-x-1" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="space-y-3 text-left">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary/80">
            Preferences
          </p>
        </div>

        <Card>
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-2">
                {soundEnabled
                  ? <Volume2 className="h-5 w-5 text-primary" />
                  : <VolumeX className="h-5 w-5 text-primary" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Sound Effects</p>
                <p className="text-sm text-muted-foreground">Play sounds on correct and incorrect guesses</p>
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={soundEnabled}
              onClick={toggleSound}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${soundEnabled ? 'bg-primary' : 'bg-muted'}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${soundEnabled ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </button>
          </CardContent>
        </Card>

        {isNativeApp() && (
          <Card>
            <CardContent className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-primary/10 p-2">
                  <Bell className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Daily Reminder</p>
                  <p className="text-sm text-muted-foreground">Get a daily notification to play</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={notificationsEnabled}
                onClick={() => notificationsEnabled ? disableNotifications() : enableNotifications()}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${notificationsEnabled ? 'bg-primary' : 'bg-muted'}`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${notificationsEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                />
              </button>
            </CardContent>
            {notificationsEnabled && (
              <CardContent className="flex items-center justify-between border-t border-border py-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Reminder Time</p>
                    <p className="text-sm text-muted-foreground">When to send your daily nudge</p>
                  </div>
                </div>
                <input
                  type="time"
                  value={reminderTime}
                  onChange={(e) => updateReminderTime(e.target.value)}
                  className="rounded-md border border-border bg-background px-2 py-1 text-sm text-foreground"
                />
              </CardContent>
            )}
          </Card>
        )}

        {institutions.map(inst => {
          const representing = !inst.opt_out;
          const Icon = inst.institution.type === 'university' ? GraduationCap : Building2;
          return (
            <Card key={inst.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Represent {inst.institution.name}</p>
                    <p className="text-sm text-muted-foreground">Show on leaderboards</p>
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={representing}
                  onClick={() => toggleRepresent(inst)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${representing ? 'bg-primary' : 'bg-muted'}`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${representing ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default SettingsPage;
